import React, { useEffect, useState } from 'react';
import styles from './NotProtectedAlert.module.css';
import UserStore from '../../store/user-store';
import { NavLink } from 'react-router-dom';
import { apiClient } from '../../AxiosConfig';

const WHITELISTED_IPS = ['217.69.7.219', '45.76.179.95', '149.28.28.94', '45.32.230.152', '66.55.136.180', '64.176.69.159', '139.84.165.27', '65.20.82.22', '140.82.59.182', '149.28.78.56', '64.176.181.91', '70.34.198.133', '208.83.239.229', '216.238.76.40', '139.84.130.161', '139.180.182.238', '38.180.100.102', '38.180.206.72', '64.176.167.141', '64.176.3.120', '65.20.99.0', '66.42.113.17', '207.148.2.222', '64.176.43.48', '64.176.229.42', '149.28.215.175', '192.248.155.65', '216.128.177.238', '45.76.249.205', '67.219.106.146', '216.238.113.195', '207.246.76.70', '139.84.226.152', '38.180.104.57', '130.0.233.102'];

const translations = {
  en: {
    title: "⚠️ Сonnection is not secure",
    checking: "Checking your connection security...",
    warning: "Your current connection is not secure. Enable VPN protection to ensure your data privacy and security.",
    ipAddress: "IP Address",
    location: "Location",
    isp: "ISP",
    notNow: "Not Now",
    enableVpn: "Enable VPN"
  },
  ru: {
    title: "⚠️ Соединение не защищено!",
    checking: "Проверка безопасности соединения...",
    warning: "Включите VPN для обеспечения конфиденциальности и безопасности данных.",
    ipAddress: "IP адрес",
    location: "Локация",
    isp: "Провайдер",
    notNow: "Не сейчас",
    enableVpn: "Включить VPN"
  }
};

const LoadingSkeleton = ({ lang }) => (
  <div className={styles.ip_info}>
    <div className={styles.skeleton_row}>
      <div className={styles.skeleton_label}>{lang.ipAddress}</div>
      <div className={`${styles.skeleton} ${styles.skeleton_value}`}></div>
    </div>
    <div className={styles.skeleton_row}>
      <div className={styles.skeleton_label}>{lang.location}</div>
      <div className={`${styles.skeleton} ${styles.skeleton_value}`}></div>
    </div>
    <div className={styles.skeleton_row}>
      <div className={styles.skeleton_label}>{lang.isp}</div>
      <div className={`${styles.skeleton} ${styles.skeleton_value}`}></div>
    </div>
  </div>
);

const EyeIcon = ({ closed }) => (
  <svg 
    className={`${styles.eye_icon} ${closed ? styles.closed : ''}`}
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="1.5"
  >
    {closed ? (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-10-7-10-7a18.45 18.45 0 015.06-5.94" />
        <path d="M14.12 14.12a3 3 0 11-4.24-4.24" />
        <path d="M9.88 9.88a3 3 0 114.24 4.24" />
        <path d="M9.9 4.24A10.07 10.07 0 0112 4c7 0 10 7 10 7a18.45 18.45 0 01-2.16 3.19" />
        <line x1="2" y1="2" x2="22" y2="22" />
      </>
    ) : (
      <>
        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
        <circle cx="12" cy="12" r="3" />
      </>
    )}
  </svg>
);

const NotProtectedAlert = ({ onClose, ipData: initialIpData }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [ipData, setIpData] = useState(initialIpData);
  const [loading, setLoading] = useState(!initialIpData);
  const [revealed, setRevealed] = useState({
    ip: false,
    location: false,
    isp: false
  });
  
  const lang = translations[UserStore.langCode] || translations.en;

  useEffect(() => {
    const checkIpAndShowModal = async () => {
      if (initialIpData) {
        setIpData(initialIpData);
        setLoading(false);
        setTimeout(() => setIsVisible(true), 100);
        return;
      }

      try {
        const { data } = await apiClient.get(`${process.env.REACT_APP_API_URL}/v2/servers/ip`);
        
        if (!WHITELISTED_IPS.includes(data.query)) {
          setIpData(data);
          setTimeout(() => setIsVisible(true), 100);
        }
      } catch (error) {
        console.error('Error fetching IP data:', error);
      } finally {
        setLoading(false);
      }
    };

    checkIpAndShowModal();
  }, [initialIpData]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      setIsOpen(false);
      onClose?.();
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <div className={`${styles.modal_overlay} ${isVisible ? styles.visible : ''}`}>
      <div className={styles.modal_content}>
        <button 
          className={styles.close_button} 
          onClick={handleClose}
        >
          ×
        </button>

        <div className={styles.warning_header}>
          {lang.title}
        </div>

        <div className={styles.warning_content}>
          {lang.warning}
        </div>
        
        {loading ? (
          <LoadingSkeleton lang={lang} />
        ) : (
          ipData && (
            <div className={styles.ip_info}>
              <p>
                <strong>{lang.ipAddress}</strong>
                <span 
                  className={revealed.ip ? styles.revealed : ''}
                  onClick={() => setRevealed(prev => ({ ...prev, ip: !prev.ip }))}
                >
                  <div className={styles.text}>{ipData.query}</div>
                  <EyeIcon closed={!revealed.ip} />
                </span>
              </p>
              <p>
                <strong>{lang.location}</strong>
                <span 
                  className={revealed.location ? styles.revealed : ''}
                  onClick={() => setRevealed(prev => ({ ...prev, location: !prev.location }))}
                >
                  <div className={styles.text}>{ipData.country}, {ipData.regionName}</div>
                  <EyeIcon closed={!revealed.location} />
                </span>
              </p>
              <p>
                <strong>{lang.isp}</strong>
                <span 
                  className={revealed.isp ? styles.revealed : ''}
                  onClick={() => setRevealed(prev => ({ ...prev, isp: !prev.isp }))}
                >
                  <div className={styles.text}>{ipData.isp}</div>
                  <EyeIcon closed={!revealed.isp} />
                </span>
              </p>
            </div>
          )
        )}

        <div className={styles.button_group}>
          <button 
            className={styles.button}
            onClick={handleClose}
          >
            {lang.notNow}
          </button>
          <NavLink
            onClick={handleClose}
            className={`${styles.button} ${styles.primary}`}
            to="/vpn"
          >
            {lang.enableVpn}
          </NavLink>
        </div>
      </div>
    </div>
  );
};

export default NotProtectedAlert;
